import { useState, useEffect, useCallback, useRef } from 'react';
import { idem } from '@/utils/api';
import { useLiveRefresh } from '@/contexts/WebSocketContext';
import { isAdmin } from '@/utils/roles';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useNotificationSound } from '@/hooks/useNotificationSound';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Users, 
  Clock, 
  CheckCircle2, 
  Bell,
  LogOut,
  UserCog,
  RefreshCw,
  MapPin,
  ShoppingCart,
  AlertCircle,
  Utensils,
  Plus,
  Minus,
  X,
  Send,
  DollarSign,
  Package,
  Volume2,
  VolumeX,
  Shield,
  Hand,
  ArrowLeft,
  User,
  Printer
} from 'lucide-react';
import axios from 'axios';
import ModernWaiter from '@/modern/staff/ModernWaiter';
import { useIsModern } from '@/modern/shared';
import { toast } from 'sonner';
import { TableMap } from '@/components/tables/TableMap';
import { TableActionsDialog } from '@/components/tables/TableActionsDialog';
import { WaiterTableList } from '@/components/tables/WaiterTableList';
import { useTableStates } from '@/components/tables/useTableStates';

// Use relative path for API calls (works on any domain)
const API = typeof window !== 'undefined' && window.location.hostname !== 'localhost' 
  ? '/api' 
  : `${process.env.REACT_APP_BACKEND_URL}/api`;

function WaiterView() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { playSound, toggleSounds } = useNotificationSound();
  
  const [spaces, setSpaces] = useState([]);
  const [selectedSpace, setSelectedSpace] = useState(null);
  const [orders, setOrders] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('tables');
  const [soundEnabled, setSoundEnabled] = useState(() => {
    return localStorage.getItem('soundsEnabled') !== 'false';
  });
  const [newOrderIds, setNewOrderIds] = useState(new Set());
  const [readyOrderIds, setReadyOrderIds] = useState(new Set());
  const previousOrdersRef = useRef([]);
  
  // Add items dialog
  const [showAddItemsDialog, setShowAddItemsDialog] = useState(false);
  const [selectedUnit, setSelectedUnit] = useState(null);
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [cart, setCart] = useState([]);
  const [orderNotes, setOrderNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  
  // Waiter calls
  const [waiterCalls, setWaiterCalls] = useState([]);
  const [actionUnit, setActionUnit] = useState(null);
  const { units: tableUnits, refresh: refreshTables, now: tablesNow } = useTableStates();
  const canSeeAmounts = ['business_admin', 'cashier', 'super_admin'].includes(user?.role);
  const rawUnit = (u) => {
    const id = u.group_primary_unit_id || u.unit_id;
    const found = spaces.flatMap(sp => sp.units || []).find(x => x.unit_id === id);
    return { ...(found || u), unit_id: id, current_session_id: u.session_id, status: u.session_id ? 'occupied' : 'available' };
  };
  const [printingTicket, setPrintingTicket] = useState(false);
  const [showTicketPreview, setShowTicketPreview] = useState(false);
  const [ticketHtml, setTicketHtml] = useState('');

  const fetchData = useCallback(async () => {
    try {
      const [dashboardRes, menuRes, notificationsRes] = await Promise.all([
        axios.get(`${API}/pos/dashboard`),
        axios.get(`${API}/menu`),
        axios.get(`${API}/notifications?type=waiter_call&acknowledged=false`).catch(() => ({ data: [] }))
      ]);
      
      // Handle waiter calls
      const calls = notificationsRes.data || [];
      if (calls.length > waiterCalls.length && soundEnabled) {
        playSound('alert');
      }
      setWaiterCalls(calls);
      
      const newOrders = dashboardRes.data.active_orders || [];
      
      // Check for new orders and ready orders
      if (previousOrdersRef.current.length > 0) {
        const previousIds = new Set(previousOrdersRef.current.map(o => o.order_id));
        const previousStatuses = new Map(previousOrdersRef.current.map(o => [o.order_id, o.status]));
        
        // Detect brand new orders
        const brandNewOrders = newOrders.filter(o => !previousIds.has(o.order_id));
        if (brandNewOrders.length > 0 && soundEnabled) {
          playSound('newOrder');
          setNewOrderIds(new Set(brandNewOrders.map(o => o.order_id)));
          setTimeout(() => setNewOrderIds(new Set()), 3000);
        }
        
        // Detect orders that became ready
        const newlyReadyOrders = newOrders.filter(o => 
          o.status === 'Listo' && previousStatuses.get(o.order_id) !== 'Listo'
        );
        if (newlyReadyOrders.length > 0 && soundEnabled) {
          playSound('orderReady');
          setReadyOrderIds(new Set(newlyReadyOrders.map(o => o.order_id)));
          setTimeout(() => setReadyOrderIds(new Set()), 3000);
        }
      }
      previousOrdersRef.current = newOrders;
      
      setSpaces(dashboardRes.data.spaces || []);
      setOrders(newOrders);
      setSessions(dashboardRes.data.active_sessions || []);
      setCategories(menuRes.data.categories || []);
      setProducts(menuRes.data.products || []);
      
      if (dashboardRes.data.spaces?.length > 0 && !selectedSpace) {
        setSelectedSpace(dashboardRes.data.spaces[0]);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  }, [selectedSpace, soundEnabled, playSound]);

  const handleToggleSound = (enabled) => {
    setSoundEnabled(enabled);
    toggleSounds(enabled);
  };

  useEffect(() => {
    fetchData();
  }, []);
  useLiveRefresh(fetchData, 15000);

  const confirmOrder = async (orderId) => {
    try {
      await axios.put(`${API}/orders/${orderId}/status`, { status: 'Confirmado' });
      toast.success('Pedido confirmado');
      
      // Auto-print comanda if enabled in settings
      try {
        const settingsRes = await axios.get(`${API}/business/settings`);
        if (settingsRes.data?.auto_print_on_confirm) {
          const printRes = await axios.post(`${API}/print/order/${orderId}`, { area: 'kitchen' });
          if (printRes.data?.html) {
            const printWindow = window.open('', '_blank', 'width=400,height=600');
            printWindow.document.write(printRes.data.html);
            printWindow.document.close();
            setTimeout(() => printWindow.print(), 500);
          }
        }
      } catch (printError) {
        console.log('Auto-print not configured or failed');
      }
      
      fetchData();
    } catch (error) {
      toast.error('Error al confirmar pedido');
    }
  };

  const markDelivered = async (orderId) => {
    try {
      await axios.put(`${API}/orders/${orderId}/status`, { status: 'Entregado' });
      toast.success('Pedido entregado');
      fetchData();
    } catch (error) {
      toast.error('Error al actualizar pedido');
    }
  };

  const acknowledgeWaiterCall = async (notificationId) => {
    try {
      await axios.post(`${API}/notifications/${notificationId}/acknowledge`);
      setWaiterCalls(waiterCalls.filter(c => c.notification_id !== notificationId));
      toast.success('Llamada atendida');
    } catch (error) {
      toast.error('Error al atender llamada');
    }
  };

  const openAddItemsDialog = async (unit) => {
    setSelectedUnit(unit);
    setCart([]);
    setOrderNotes('');
    setSelectedCategory(null);
    
    // Check if unit has active session, if not create one
    const unitSession = sessions.find(s => s.unit_id === unit.unit_id);
    if (!unitSession && unit.status === 'available') {
      try {
        await axios.post(`${API}/sessions`, { unit_id: unit.unit_id });
        await fetchData();
      } catch (error) {
        toast.error('Error al abrir mesa');
        return;
      }
    }
    
    setShowAddItemsDialog(true);
  };

  const addToCart = (product) => {
    const existingIndex = cart.findIndex(item => item.product_id === product.product_id);
    if (existingIndex >= 0) {
      const newCart = [...cart];
      newCart[existingIndex].quantity += 1;
      setCart(newCart);
    } else {
      setCart([...cart, {
        product_id: product.product_id,
        product: product,
        quantity: 1,
        notes: ''
      }]);
    }
  };

  const updateCartQuantity = (index, delta) => {
    const newCart = [...cart];
    newCart[index].quantity += delta;
    if (newCart[index].quantity <= 0) {
      newCart.splice(index, 1);
    }
    setCart(newCart);
  };

  const submitOrder = async () => {
    if (cart.length === 0) {
      toast.error('Agrega items al pedido');
      return;
    }
    
    setSubmitting(true);
    try {
      const unitSession = sessions.find(s => s.unit_id === selectedUnit.unit_id);
      if (!unitSession) {
        toast.error('No hay sesión activa');
        return;
      }
      
      await axios.post(`${API}/sessions/${unitSession.session_id}/orders`, {
        items: cart.map(item => ({
          product_id: item.product_id,
          quantity: item.quantity,
          notes: item.notes
        })),
        notes: orderNotes
      }, idem());
      
      toast.success('¡Pedido enviado!');
      setShowAddItemsDialog(false);
      setCart([]);
      fetchData();
    } catch (error) {
      toast.error('Error al enviar pedido');
    } finally {
      setSubmitting(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  // Print ticket function - shows preview first based on settings
  const handlePrintTicket = async (sessionId, isWaiterTicket = false) => {
    if (!sessionId) return;
    
    setPrintingTicket(true);
    try {
      // Get settings to check if preview is enabled
      let showPreview = true;
      try {
        const settingsRes = await axios.get(`${API}/business/settings`);
        showPreview = settingsRes.data?.show_print_preview !== false;
      } catch (e) {}
      
      // Use waiter ticket endpoint for quick price display
      const endpoint = isWaiterTicket 
        ? `${API}/print/waiter-ticket/${sessionId}`
        : `${API}/print/ticket/${sessionId}`;
      
      const response = await axios.post(
        endpoint,
        isWaiterTicket ? { include_prices: true } : { include_tips_suggestion: true }
      );
      
      const { html } = response.data;
      
      if (html) {
        if (showPreview) {
          setTicketHtml(html);
          setShowTicketPreview(true);
        } else {
          const printWindow = window.open('', '_blank', 'width=400,height=600');
          printWindow.document.write(html);
          printWindow.document.close();
        }
      }
    } catch (error) {
      console.error('Print error:', error);
      toast.error('Error al generar ticket');
    } finally {
      setPrintingTicket(false);
    }
  };

  // Actually print after preview
  const handleConfirmPrint = () => {
    if (!ticketHtml) return;
    
    const printWindow = window.open('', '_blank', 'width=400,height=600');
    printWindow.document.write(ticketHtml);
    printWindow.document.close();
    setShowTicketPreview(false);
    setTicketHtml('');
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'occupied': return 'from-violet-500 to-purple-600';
      case 'available': return 'from-emerald-500 to-green-600';
      case 'reserved': return 'from-amber-500 to-yellow-600';
      default: return 'from-slate-500 to-gray-600';
    }
  };

  const newOrders = orders.filter(o => o.status === 'Nuevo').length;
  const readyOrders = orders.filter(o => o.status === 'Listo').length;
  const filteredProducts = selectedCategory 
    ? products.filter(p => p.category_id === selectedCategory)
    : products;
  const cartTotal = cart.reduce((sum, item) => sum + (item.product.price * item.quantity), 0);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-violet-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-violet-600 dark:text-violet-300">Cargando...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-card/95 backdrop-blur-xl border-b border-violet-500/20">
        <div className="px-4 md:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4">
            {/* Back to Dashboard */}
            {isAdmin(user) && (
            <Button 
              variant="ghost" 
              size="icon"
              onClick={() => navigate('/dashboard')}
              className="text-foreground/70 hover:text-foreground hover:bg-accent/10"
              title="Volver al Panel"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            )}
            <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center shadow-lg shadow-violet-500/30">
              <Users className="w-7 h-7 text-foreground" />
            </div>
            <div>
              <h1 className="font-bold text-xl text-foreground">MOZO</h1>
              <p className="text-sm text-violet-700">{user?.name}</p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            {newOrders > 0 && (
              <motion.div 
                initial={{ scale: 0.8 }}
                animate={{ scale: [1, 1.1, 1] }}
                transition={{ repeat: Infinity, duration: 1.5 }}
                className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-blue-500 text-foreground text-sm"
              >
                <Bell className="w-4 h-4" />
                <span className="font-bold">{newOrders}</span>
                <span className="hidden sm:inline">nuevos</span>
              </motion.div>
            )}
            {readyOrders > 0 && (
              <motion.div 
                initial={{ scale: 0.8 }}
                animate={{ scale: [1, 1.1, 1] }}
                transition={{ repeat: Infinity, duration: 1.5 }}
                className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-green-500 text-foreground text-sm"
              >
                <CheckCircle2 className="w-4 h-4" />
                <span className="font-bold">{readyOrders}</span>
                <span className="hidden sm:inline">listos</span>
              </motion.div>
            )}
            <Button 
              variant="ghost" 
              size="icon" 
              onClick={() => handleToggleSound(!soundEnabled)}
              className="text-foreground/70 hover:text-foreground hover:bg-accent/10"
              title={soundEnabled ? 'Desactivar sonido' : 'Activar sonido'}
            >
              {soundEnabled ? (
                <Volume2 className="w-5 h-5 text-green-400" />
              ) : (
                <VolumeX className="w-5 h-5" />
              )}
            </Button>
            <Button variant="ghost" size="icon" onClick={fetchData} className="text-foreground/70 hover:text-foreground hover:bg-accent/10">
              <RefreshCw className="w-5 h-5" />
            </Button>
            <Button variant="ghost" size="icon" onClick={() => navigate('/perfil')} className="text-foreground/70 hover:text-foreground hover:bg-accent/10" data-testid="profile-link" title="Mi perfil y apariencia">
              <UserCog className="w-5 h-5" />
            </Button>
            <Button variant="ghost" size="icon" onClick={handleLogout} className="text-foreground/70 hover:text-foreground hover:bg-accent/10">
              <LogOut className="w-5 h-5" />
            </Button>
          </div>
        </div>
      </header>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <div className="sticky top-16 z-40 bg-card/95 backdrop-blur-xl border-b border-border">
          <TabsList className="w-full justify-start rounded-none h-14 bg-transparent p-0 px-4">
            <TabsTrigger 
              value="tables" 
              className="flex-1 max-w-[200px] rounded-none border-b-2 border-transparent data-[state=active]:border-violet-500 data-[state=active]:bg-transparent data-[state=active]:text-violet-700 text-slate-600"
            >
              <MapPin className="w-4 h-4 mr-2" />
              Mesas
            </TabsTrigger>
            <TabsTrigger 
              value="orders"
              className="flex-1 max-w-[200px] rounded-none border-b-2 border-transparent data-[state=active]:border-violet-500 data-[state=active]:bg-transparent data-[state=active]:text-violet-700 text-slate-600 relative"
            >
              <ShoppingCart className="w-4 h-4 mr-2" />
              Pedidos
              {(newOrders + readyOrders) > 0 && (
                <span className="ml-2 w-6 h-6 bg-red-500 rounded-full text-xs flex items-center justify-center text-white font-bold">
                  {newOrders + readyOrders}
                </span>
              )}
            </TabsTrigger>
          </TabsList>
        </div>

        {/* Waiter Calls Alert */}
        {waiterCalls.length > 0 && (
          <div className="mx-4 mt-4">
            <motion.div
              initial={{ scale: 0.95 }}
              animate={{ scale: [1, 1.02, 1] }}
              transition={{ repeat: Infinity, duration: 1.5 }}
            >
              <Card className="bg-amber-500/20 border-amber-500 shadow-lg shadow-amber-500/20">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <Hand className="w-5 h-5 text-amber-400 animate-bounce" />
                      <span className="font-bold text-amber-800">¡Mesas llamando!</span>
                    </div>
                    <Badge className="bg-amber-500">{waiterCalls.length}</Badge>
                  </div>
                  <div className="space-y-2">
                    {waiterCalls.map((call) => (
                      <div key={call.notification_id} className="flex items-center justify-between bg-muted/50 rounded-lg p-3">
                        <div>
                          <span className="font-bold text-foreground">{call.unit_name}</span>
                          <span className="text-xs text-muted-foreground ml-2">
                            {new Date(call.created_at).toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit' })}
                          </span>
                        </div>
                        <Button 
                          size="sm"
                          className="bg-amber-700 hover:bg-amber-800"
                          onClick={() => acknowledgeWaiterCall(call.notification_id)}
                        >
                          <CheckCircle2 className="w-4 h-4 mr-1" />
                          Atender
                        </Button>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          </div>
        )}

        {/* Tables Tab */}
        <TabsContent value="tables" className="mt-0 p-4">
          {/* Space selector */}
          {spaces.length > 1 && (
            <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
              {spaces.map((space) => (
                <Button
                  key={space.space_id}
                  variant={selectedSpace?.space_id === space.space_id ? 'default' : 'outline'}
                  size="sm"
                  className={selectedSpace?.space_id === space.space_id 
                    ? 'bg-violet-600 hover:bg-violet-700' 
                    : 'border-input text-slate-600 hover:bg-muted'
                  }
                  onClick={() => setSelectedSpace(space)}
                >
                  {space.name}
                </Button>
              ))}
            </div>
          )}

          {/* Mesas: celular = lista por urgencia; pantallas grandes = mapa unificado */}
          <div className="md:hidden">
            <WaiterTableList units={tableUnits} userId={user?.user_id} defaultMine={user?.role === 'waiter'} now={tablesNow} onSelect={setActionUnit} />
          </div>
          <div className="hidden md:block">
            <TableMap
              units={selectedSpace ? tableUnits.filter(u => u.space_id === selectedSpace.space_id) : tableUnits}
              mapImage={selectedSpace?.map_image_url}
              showAmount={canSeeAmounts}
              now={tablesNow}
              selectedId={actionUnit?.unit_id}
              onSelect={setActionUnit}
              onClean={async (u) => { try { await axios.post(`${API}/tables/${u.unit_id}/clean`); toast.success('Mesa lista'); refreshTables(); } catch { toast.error('No se pudo marcar'); } }}
            />
          </div>
        </TabsContent>

        {/* Orders Tab */}
        <TabsContent value="orders" className="mt-0 p-4">
          <div className="space-y-6">
            {orders.length === 0 ? (
              <div className="text-center py-12">
                <ShoppingCart className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
                <p className="text-muted-foreground">No hay pedidos activos</p>
              </div>
            ) : (
              <>
                {/* New orders */}
                {orders.filter(o => o.status === 'Nuevo').length > 0 && (
                  <div>
                    <h3 className="text-lg font-bold text-blue-400 mb-4 flex items-center gap-2">
                      <AlertCircle className="w-5 h-5" />
                      Pendientes de confirmar ({orders.filter(o => o.status === 'Nuevo').length})
                    </h3>
                    <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                      <AnimatePresence>
                      {orders.filter(o => o.status === 'Nuevo').map((order) => {
                        const isNew = newOrderIds.has(order.order_id);
                        return (
                        <motion.div
                          key={order.order_id}
                          initial={{ scale: 0.9, opacity: 0 }}
                          animate={{ 
                            scale: 1, 
                            opacity: 1,
                            boxShadow: isNew ? ['0 0 0 0 rgba(59, 130, 246, 0)', '0 0 0 8px rgba(59, 130, 246, 0.3)', '0 0 0 0 rgba(59, 130, 246, 0)'] : 'none'
                          }}
                          exit={{ scale: 0.9, opacity: 0 }}
                          transition={{ duration: 0.3 }}
                        >
                        <Card className={`bg-blue-950/30 border-blue-500/30 ${isNew ? 'ring-2 ring-blue-500' : ''}`}>
                          <CardContent className="p-4">
                            <div className="flex items-center justify-between mb-3">
                              <div className="flex items-center gap-3">
                                <Badge className="bg-blue-500">Nuevo</Badge>
                                <span className="font-bold text-foreground">{order.unit_name || 'Mesa'}</span>
                                <span className="text-xs text-muted-foreground">#{order.order_id?.slice(-6)}</span>
                              </div>
                              <span className="font-bold text-emerald-400">${(order.total || 0).toLocaleString()}</span>
                            </div>
                            {order.participant_name && (
                              <div className="mb-2">
                                <Badge variant="outline" className="text-xs border-violet-400 text-violet-400">
                                  <User className="w-3 h-3 mr-1" />
                                  {order.participant_name}
                                </Badge>
                              </div>
                            )}
                            <div className="space-y-1 mb-4 bg-muted/50 p-2 rounded-lg">
                              {order.items?.slice(0, 4).map((item, idx) => (
                                <p key={idx} className="text-sm text-muted-foreground">
                                  <span className="font-medium text-foreground">{item.quantity}x</span> {item.product_name}
                                </p>
                              ))}
                              {order.items?.length > 4 && (
                                <p className="text-xs text-muted-foreground italic">+{order.items.length - 4} más...</p>
                              )}
                            </div>
                            <Button 
                              className="w-full bg-blue-600 hover:bg-blue-700"
                              onClick={() => confirmOrder(order.order_id)}
                            >
                              <CheckCircle2 className="w-4 h-4 mr-2" />
                              Confirmar pedido
                            </Button>
                          </CardContent>
                        </Card>
                        </motion.div>
                        );
                      })}
                      </AnimatePresence>
                    </div>
                  </div>
                )}

                {/* Ready orders */}
                {orders.filter(o => o.status === 'Listo').length > 0 && (
                  <div>
                    <h3 className="text-lg font-bold text-green-400 mb-4 flex items-center gap-2">
                      <CheckCircle2 className="w-5 h-5" />
                      Listos para entregar ({orders.filter(o => o.status === 'Listo').length})
                    </h3>
                    <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                      <AnimatePresence>
                      {orders.filter(o => o.status === 'Listo').map((order) => {
                        const isReady = readyOrderIds.has(order.order_id);
                        return (
                        <motion.div
                          key={order.order_id}
                          initial={{ scale: 0.9, opacity: 0 }}
                          animate={{ 
                            scale: isReady ? [1, 1.02, 1] : 1, 
                            opacity: 1 
                          }}
                          exit={{ scale: 0.9, opacity: 0 }}
                          transition={{ duration: 0.3, repeat: isReady ? Infinity : 0, repeatDelay: 1 }}
                        >
                        <Card className={`bg-green-950/30 border-green-500/30 ${isReady ? 'ring-2 ring-green-500 animate-pulse' : ''}`}>
                          <CardContent className="p-4">
                            <div className="flex items-center justify-between mb-3">
                              <div className="flex items-center gap-3">
                                <Badge className="bg-green-500">Listo</Badge>
                                <span className="font-bold text-foreground">{order.unit_name || 'Mesa'}</span>
                              </div>
                              <span className="font-bold text-emerald-400">${(order.total || 0).toLocaleString()}</span>
                            </div>
                            {order.participant_name && (
                              <div className="mb-2">
                                <Badge variant="outline" className="text-xs border-violet-400 text-violet-400">
                                  <User className="w-3 h-3 mr-1" />
                                  {order.participant_name}
                                </Badge>
                              </div>
                            )}
                            <div className="space-y-1 mb-4 bg-muted/50 p-2 rounded-lg">
                              {order.items?.map((item, idx) => (
                                <p key={idx} className="text-sm text-muted-foreground">
                                  <span className="font-medium text-foreground">{item.quantity}x</span> {item.product_name}
                                </p>
                              ))}
                            </div>
                            <Button 
                              className="w-full bg-green-600 hover:bg-green-700"
                              onClick={() => markDelivered(order.order_id)}
                            >
                              <Utensils className="w-4 h-4 mr-2" />
                              Marcar entregado
                            </Button>
                          </CardContent>
                        </Card>
                        </motion.div>
                        );
                      })}
                      </AnimatePresence>
                    </div>
                  </div>
                )}

                {/* In progress */}
                {orders.filter(o => ['Confirmado', 'En preparación'].includes(o.status)).length > 0 && (
                  <div>
                    <h3 className="text-lg font-bold text-orange-400 mb-4 flex items-center gap-2">
                      <Clock className="w-5 h-5" />
                      En preparación ({orders.filter(o => ['Confirmado', 'En preparación'].includes(o.status)).length})
                    </h3>
                    <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                      {orders.filter(o => ['Confirmado', 'En preparación'].includes(o.status)).map((order) => (
                        <Card key={order.order_id} className="bg-muted/50 border-border">
                          <CardContent className="p-4">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-3">
                                <Badge className={order.status === 'Confirmado' ? 'bg-purple-500/20 text-purple-400 border-purple-500/30' : 'bg-orange-500/20 text-orange-400 border-orange-500/30'}>
                                  {order.status}
                                </Badge>
                                <span className="font-bold text-foreground">{order.unit_name || 'Mesa'}</span>
                              </div>
                              <div className="text-right">
                                <p className="font-bold text-emerald-400">${(order.total || 0).toLocaleString()}</p>
                                <p className="text-xs text-muted-foreground">{order.items?.length || 0} items</p>
                              </div>
                            </div>
                            {order.participant_name && (
                              <div className="mt-2">
                                <Badge variant="outline" className="text-xs border-violet-400 text-violet-400">
                                  <User className="w-3 h-3 mr-1" />
                                  {order.participant_name}
                                </Badge>
                              </div>
                            )}
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </TabsContent>
      </Tabs>

      {/* Add Items Dialog */}
      <Dialog open={showAddItemsDialog} onOpenChange={setShowAddItemsDialog}>
        <DialogContent className="max-w-4xl max-h-[90vh] bg-card border-border text-foreground p-0">
          <DialogHeader className="p-6 pb-0">
            <DialogTitle className="flex items-center gap-3 text-xl">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center">
                <Plus className="w-6 h-6" />
              </div>
              Agregar pedido - {selectedUnit?.name}
            </DialogTitle>
            <DialogDescription className="text-muted-foreground">
              Selecciona los productos para agregar al pedido
            </DialogDescription>
          </DialogHeader>

          <div className="flex flex-col md:flex-row h-[60vh]">
            {/* Products section */}
            <div className="flex-1 p-6 border-r border-border">
              {/* Category tabs */}
              <div className="flex gap-2 mb-4 overflow-x-auto pb-2">
                <Button
                  size="sm"
                  variant={!selectedCategory ? 'default' : 'outline'}
                  className={!selectedCategory ? 'bg-violet-600' : 'border-input'}
                  onClick={() => setSelectedCategory(null)}
                >
                  Todo
                </Button>
                {categories.map((cat) => (
                  <Button
                    key={cat.category_id}
                    size="sm"
                    variant={selectedCategory === cat.category_id ? 'default' : 'outline'}
                    className={selectedCategory === cat.category_id ? 'bg-violet-600' : 'border-input'}
                    onClick={() => setSelectedCategory(cat.category_id)}
                  >
                    {cat.name}
                  </Button>
                ))}
              </div>

              {/* Products grid */}
              <ScrollArea className="h-[calc(60vh-120px)]">
                <div className="grid grid-cols-2 gap-3">
                  {filteredProducts.map((product) => (
                    <Card 
                      key={product.product_id}
                      className="bg-muted border-border cursor-pointer hover:bg-accent transition-colors"
                      onClick={() => addToCart(product)}
                    >
                      <CardContent className="p-3">
                        <p className="font-semibold text-foreground text-sm truncate">{product.name}</p>
                        <p className="text-emerald-600 dark:text-emerald-400 font-bold">${product.price.toLocaleString()}</p>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </ScrollArea>
            </div>

            {/* Cart section */}
            <div className="w-full md:w-80 p-6 bg-muted flex flex-col">
              <h3 className="font-bold text-lg mb-4 flex items-center gap-2">
                <ShoppingCart className="w-5 h-5 text-violet-600 dark:text-violet-400" />
                Pedido ({cart.length})
              </h3>

              {cart.length === 0 ? (
                <div className="flex-1 flex items-center justify-center">
                  <p className="text-muted-foreground text-center">
                    Toca un producto para agregarlo
                  </p>
                </div>
              ) : (
                <>
                  <ScrollArea className="flex-1 mb-4">
                    <div className="space-y-3">
                      {cart.map((item, index) => (
                        <div key={index} className="flex items-center gap-3 p-3 bg-background rounded-xl">
                          <div className="flex-1 min-w-0">
                            <p className="font-semibold text-foreground text-sm truncate">{item.product.name}</p>
                            <p className="text-emerald-600 dark:text-emerald-400 text-sm">${(item.product.price * item.quantity).toLocaleString()}</p>
                          </div>
                          <div className="flex items-center gap-2">
                            <Button 
                              size="icon" 
                              variant="outline" 
                              className="w-8 h-8 border-input"
                              onClick={() => updateCartQuantity(index, -1)}
                            >
                              <Minus className="w-4 h-4" />
                            </Button>
                            <span className="w-8 text-center font-bold">{item.quantity}</span>
                            <Button 
                              size="icon" 
                              variant="outline" 
                              className="w-8 h-8 border-input"
                              onClick={() => updateCartQuantity(index, 1)}
                            >
                              <Plus className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </ScrollArea>

                  <Textarea
                    placeholder="Notas del pedido (opcional)"
                    value={orderNotes}
                    onChange={(e) => setOrderNotes(e.target.value)}
                    className="mb-4 bg-background border-input text-foreground"
                    rows={2}
                  />

                  <div className="flex justify-between items-center mb-4 text-lg font-bold">
                    <span>Total</span>
                    <span className="text-emerald-600 dark:text-emerald-400">${cartTotal.toLocaleString()}</span>
                  </div>

                  <Button 
                    className="w-full h-12 bg-gradient-to-r from-violet-500 to-purple-600 hover:from-violet-600 hover:to-purple-700"
                    onClick={submitOrder}
                    disabled={submitting}
                  >
                    {submitting ? (
                      <RefreshCw className="w-5 h-5 animate-spin mr-2" />
                    ) : (
                      <Send className="w-5 h-5 mr-2" />
                    )}
                    Enviar Pedido
                  </Button>
                </>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Ticket Preview Modal */}
      <Dialog open={showTicketPreview} onOpenChange={setShowTicketPreview}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Printer className="w-5 h-5 text-blue-500" />
              Vista Previa del Ticket
            </DialogTitle>
          </DialogHeader>
          
          <div className="flex-1 overflow-auto bg-white rounded-lg border shadow-inner my-4" style={{ maxHeight: '50vh' }}>
            <iframe
              srcDoc={ticketHtml}
              title="Ticket Preview"
              className="w-full h-full min-h-[400px]"
              style={{ border: 'none' }}
            />
          </div>
          
          <DialogFooter className="flex gap-2">
            <Button variant="outline" onClick={() => setShowTicketPreview(false)}>
              Cancelar
            </Button>
            <Button 
              onClick={handleConfirmPrint}
              className="bg-gradient-to-r from-blue-500 to-cyan-600"
            >
              <Printer className="w-4 h-4 mr-2" />
              Imprimir
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <TableActionsDialog
        unit={actionUnit}
        units={tableUnits}
        open={!!actionUnit}
        onOpenChange={(v) => !v && setActionUnit(null)}
        onDone={() => { refreshTables(); fetchData(); }}
        detailsLabel="Agregar pedido"
        onOpenDetails={(u) => openAddItemsDialog(rawUnit(u))}
        onOpenTable={(u) => openAddItemsDialog(rawUnit(u))}
        renderExtra={(u) => u.session_id && (
          <Button variant="outline" data-testid="table-action-print-bill" disabled={printingTicket}
            onClick={() => handlePrintTicket(u.session_id, false)}>
            <Printer className="w-4 h-4 mr-2" />Imprimir pre-cuenta
          </Button>
        )}
      />
    </div>
  );
}

export default function WaiterViewPage() {
  return useIsModern() ? <ModernWaiter /> : <WaiterView />;
}
